/**
 * smTransitionActions — the decoupled seam for firing a state-machine
 * transition's Actions (ENG-1062).
 *
 * A transition edge can carry `data.actions: string[]` — the names of custom
 * events to fire once, at the moment the transition is taken. GraphEvaluator
 * (body/face render hot path) calls runTransitionActions() from
 * updateSMTransitions, and App.jsx installs a handler that routes each name to
 * EventRuntime.fireCustomEvent.
 *
 * Why a settable handler instead of a direct import: GraphEvaluator must NOT
 * import EventRuntime (that pulls the entire event/asset/network dependency
 * graph onto the animation hot path). This mirrors the existing
 * cameraController.setHeadOffsetSource / EventRuntime.setSendTextInput pattern,
 * and — being import-free — is unit-testable under plain `node --test`, which
 * GraphEvaluator itself is not.
 *
 * This is NOT a hot path: it runs only when a transition is actually taken
 * (once), never per frame, so it can allocate freely — but it deliberately
 * doesn't, and returns immediately when there are no actions.
 */

let _handler = null

/**
 * Install the fire handler. App.jsx wires this to
 * (name, ctx) => EventRuntime.fireCustomEvent(name, ctx); pass null to clear.
 * @param {((name: string, ctx: object) => void) | null} fn
 */
export function setTransitionActionHandler(fn) {
  _handler = fn
}

/**
 * Fire each named action once, in authored order. No-op when the handler is
 * unset (e.g. before App wires it, or in a unit test that doesn't install one)
 * or when there are no actions. Blank names are skipped — an unnamed custom
 * event can never be fired.
 * @param {string[] | undefined | null} actions
 * @param {object} ctx  payload passed to the handler, e.g. { smNodeId, from, to }
 */
export function runTransitionActions(actions, ctx) {
  if (!_handler || !actions || actions.length === 0) return
  for (const name of actions) {
    if (name && String(name).trim()) _handler(name, ctx)
  }
}
